/**
 * Shared helpers for formatting dates and figures with `Intl` at render time
 * (SL-1908): a guard so a bad epoch never reaches `Intl` (which throws a
 * RangeError in the middle of a render), the space normalization the inboxes use,
 * and a cache of `Intl` formatter instances — building one is far costlier than
 * calling `.format()`, and a list formats one date per row on every render.
 *
 * Pair with `intlLocale` (same folder) to turn the app's language into a locale.
 */

/**
 * An epoch `Intl` can format. Backend payloads are cast, not parsed, so a truthy
 * bad value ('abc', 8.7e15 — past the ±8.64e15 Date range) can reach a formatter.
 */
export function isFormattableEpoch(value: unknown): value is number {
  return (
    typeof value === 'number' && Number.isFinite(value) && !Number.isNaN(new Date(value).getTime())
  );
}

/**
 * Recent ICU puts a narrow no-break space (U+202F) — sometimes U+00A0 — before
 * AM/PM and inside "p. m."; older formatters wrote a plain space. Normalizing
 * keeps English byte-identical and every language stable across ICU versions.
 */
export const plainSpaces = (text: string): string => text.replace(/[  ]/g, ' ');

const formatters = new Map<string, unknown>();

function cached<T>(kind: string, locale: string | undefined, options: object, build: () => T): T {
  const key = `${kind}|${locale ?? ''}|${JSON.stringify(options)}`;
  let formatter = formatters.get(key) as T | undefined;
  if (!formatter) {
    formatter = build();
    formatters.set(key, formatter);
  }
  return formatter;
}

/** A shared `Intl.DateTimeFormat` for `locale` + `options`. May throw on a bad locale. */
export function dateTimeFormat(
  locale: string | undefined,
  options: Intl.DateTimeFormatOptions = {},
): Intl.DateTimeFormat {
  return cached('dt', locale, options, () => new Intl.DateTimeFormat(locale, options));
}

/** A shared `Intl.RelativeTimeFormat` for `locale` + `options`. May throw on a bad locale. */
export function relativeTimeFormat(
  locale: string | undefined,
  options: Intl.RelativeTimeFormatOptions = {},
): Intl.RelativeTimeFormat {
  return cached('rt', locale, options, () => new Intl.RelativeTimeFormat(locale, options));
}

/** A shared `Intl.NumberFormat` for `locale` + `options`. May throw on a bad locale. */
export function numberFormat(
  locale: string | undefined,
  options: Intl.NumberFormatOptions = {},
): Intl.NumberFormat {
  return cached('nf', locale, options, () => new Intl.NumberFormat(locale, options));
}
